import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { AuthAPI, tokenStore, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Muat sesi dari token tersimpan saat aplikasi start.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tokenStore.get()) { setLoading(false); return; }
      try {
        const { user } = await AuthAPI.me();
        if (alive) setUser(user);
      } catch {
        tokenStore.clear();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const login = useCallback(async (identifier, password) => {
    const { token, user } = await AuthAPI.login(identifier, password);
    tokenStore.set(token);
    setUser(user);
    return user;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await AuthAPI.register(payload);
    // Bila akun menunggu verifikasi (tanpa token), jangan login otomatis.
    if (data.token) { tokenStore.set(data.token); setUser(data.user); }
    return data;
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    setUser(null);
  }, []);

  // Perbarui profil dasar & sinkronkan state user.
  const updateProfile = useCallback(async (payload) => {
    const updated = await AuthAPI.updateMe(payload);
    setUser(updated);
    return updated;
  }, []);

  const isAdmin = user && [
    "SUPER_ADMIN", "ADMIN_PUSAT", "ADMIN_WILAYAH", "ADMIN_DAERAH", "ADMIN_CABANG",
    "ADMIN_RANTING", "ADMIN_GERAI",
    "ADMIN_KEUANGAN", "ADMIN_KEUANGAN_WILAYAH", "ADMIN_KEUANGAN_DAERAH",
    "ADMIN_KEUANGAN_CABANG", "ADMIN_KEUANGAN_RANTING", "ADMIN_KEUANGAN_GERAI",
    "PENGAWAS",
  ].includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, isAdmin, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth harus dipakai di dalam AuthProvider");
  return ctx;
};
